import api, { dataAPI } from './api';
import { chunkItems } from '../utils/importParsers';

// El servidor importa con POST /data/bulk-import: crea un hilo en segundo plano
// (máx. 2 a la vez por tarjeta) y se consulta con GET /data/thread/:id.
// Ojo: POST /data/:rfidId/batch NO sirve para esto, es de lectura (devuelve items por ids).

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Espera a que un hilo del servidor termine. Devuelve el hilo final ({status, result, error}). */
export async function waitForThread(threadId, { onProgress, intervalMs = 500, timeoutMs = 10 * 60 * 1000 } = {}) {
  const deadline = Date.now() + timeoutMs;
  let failures = 0;
  while (Date.now() < deadline) {
    try {
      const { data } = await api.get(`/data/thread/${threadId}`, { timeout: 15000 });
      failures = 0;
      onProgress?.(data.progress || 0, data.message || '');
      if (['completed', 'failed', 'cancelled'].includes(data.status)) return data;
    } catch (err) {
      // 404 = el hilo ya no existe (caducó o es de otra sesión): no tiene sentido seguir.
      if (err.response?.status === 404 || ++failures >= 5) throw err;
    }
    await sleep(intervalMs);
  }
  throw new Error('timeout');
}

async function startBulkImport(rfidId, items) {
  for (let attempt = 0; ; attempt++) {
    try {
      const { data } = await api.post('/data/bulk-import', { rfidId, items }, { timeout: 60000 });
      return data.threadId;
    } catch (err) {
      // 429: ya hay otras 2 operaciones (p. ej. una exportación) en marcha para esta tarjeta.
      if (err.response?.status === 429 && attempt < 6) { await sleep(3000); continue; }
      throw err;
    }
  }
}

/**
 * Importa `items` ([{name, description, data, tags}]) en lotes secuenciales.
 * Cada lote es una transacción en el servidor: si uno falla, ese lote no entra
 * entero y se para ahí (lo ya importado se queda; repetir la importación con
 * "saltar duplicados" continúa donde se quedó).
 *
 * onProgress(done, total, message) · shouldCancel() se mira entre lotes.
 * Devuelve { success, failed, total, cancelled, error }.
 */
export async function importInChunks(rfidId, items, { onProgress, shouldCancel } = {}) {
  const chunks = chunkItems(items);
  const total = items.length;
  const out = { success: 0, failed: 0, total, cancelled: false, error: null };
  let done = 0;
  onProgress?.(0, total, '');

  for (const chunk of chunks) {
    if (shouldCancel?.()) { out.cancelled = true; break; }
    try {
      const base = done;
      const threadId = await startBulkImport(rfidId, chunk);
      const thread = await waitForThread(threadId, {
        onProgress: (pct, msg) => onProgress?.(base + Math.round((chunk.length * pct) / 100), total, msg),
      });
      if (thread.status !== 'completed') {
        out.failed += total - done;
        out.error = thread.error || thread.status;
        break;
      }
      const r = thread.result || {};
      out.success += r.success || 0;
      out.failed += r.failed || 0;
      if (r.success === 0 && r.failed > 0) { // la transacción del lote se revirtió
        out.failed += total - done - chunk.length;
        out.error = r.errors?.[0]?.error || 'import_failed';
        break;
      }
    } catch (err) {
      out.failed += total - done;
      out.error = err.response?.data?.error || err.message;
      break;
    }
    done += chunk.length;
    onProgress?.(done, total, '');
  }
  return out;
}

/**
 * Items de una bóveda (name, username, url) para detectar duplicados. Pagina por
 * número de página sobre toda la cuenta (descifra 100 filas por petición) y filtra
 * en el cliente: el filtro `vault` del servidor descifra la cuenta entera en cada
 * página. No usa el cursor: con servidores sin el arreglo de createdAtRaw, el
 * cursor se salta las filas de un mismo lote de importación.
 */
export async function fetchVaultItems(rfidId, vaultId) {
  const found = [];
  const limit = 100;
  for (let page = 1; page <= 500; page++) {
    const { data } = await dataAPI.list(rfidId, { limit, page });
    const list = data.data || [];
    for (const it of list) {
      const d = typeof it.data === 'object' && it.data ? it.data : {};
      if (it.name === '__otp_entries__' || d.type === 'otp_vault') continue;
      if ((d.vault || 'personal') !== vaultId) continue;
      found.push({ name: it.name, username: d.username || '', url: d.url || '' });
    }
    if (list.length < limit) break;
  }
  return found;
}
